/**
 * Fabric selection modal (PDP)
 *
 * - Before modal apply: "Wybrana tkanina" follows the selected product variant.
 * - After modal apply: show modal fabric and clear variant fabric swatches.
 * - Clicking a fabric variant again adopts that variant into "Wybrana tkanina" immediately.
 */
(() => {
  const SELECTED_CLASS = 'is-selected';
  const ACTIVE_CLASS = 'is-active';

  class FabricSelector extends HTMLElement {
    connectedCallback() {
      this.modalId = this.dataset.modalId;
      this.formId = this.dataset.formId;
      this.propertyName = this.dataset.propertyName || 'Tkanina';
      this.fabricOptionName = this.dataset.fabricOptionName || '';
      this.modalFabricSelected = this.dataset.modalFabricSelected === 'true';

      this.selectedNameEl = this.querySelector('[data-fabric-selected-name]');
      this.selectedImageEl = this.querySelector('[data-fabric-selected-image]');
      this.propertyInput = this.querySelector('[data-fabric-property]');
      this.idPropertyInput = this.querySelector('[data-fabric-id-property]');
      this.collectionPropertyInput = this.querySelector('[data-fabric-collection-property]');
      this.catalog = this.readCatalog();

      this._onFabricApplied = this.onFabricApplied.bind(this);
      this._onVariantChange = this.onVariantChange.bind(this);
      this._onVariantOptionChange = this.onVariantOptionChange.bind(this);

      document.addEventListener('fabric:applied', this._onFabricApplied);
      // Immediate sync on fabric option change (do not wait for section fetch).
      document.addEventListener('change', this._onVariantOptionChange, true);

      if (window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
        this._variantUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          this._onVariantChange
        );
      }

      this.restoreModalSelectionIfNeeded();
    }

    disconnectedCallback() {
      document.removeEventListener('fabric:applied', this._onFabricApplied);
      document.removeEventListener('change', this._onVariantOptionChange, true);
      if (typeof this._variantUnsubscriber === 'function') {
        this._variantUnsubscriber();
      }
    }

    storageKey() {
      return `fabric-modal-selection:${this.formId || this.modalId || ''}`;
    }

    restoreModalSelectionIfNeeded() {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(this.storageKey()) || 'null');
      } catch (e) {
        saved = null;
      }

      if (!saved?.name && !this.modalFabricSelected) return;

      this.modalFabricSelected = true;
      this.dataset.modalFabricSelected = 'true';
      this.dataset.fromVariant = 'false';

      if (saved?.name) {
        this.applySelection(saved, { fromModal: true });
      }

      this.clearFabricVariantSelection();
      this.resetProductMediaToDefault();
      requestAnimationFrame(() => {
        this.clearFabricVariantSelection();
        this.resetProductMediaToDefault();
      });
    }

    lockModalFabric(detail) {
      this.modalFabricSelected = true;
      this.dataset.modalFabricSelected = 'true';
      this.dataset.fromVariant = 'false';

      if (detail?.name) {
        try {
          sessionStorage.setItem(this.storageKey(), JSON.stringify(detail));
        } catch (e) {
          /* ignore quota */
        }
        this.applySelection(detail, { fromModal: true });
      }

      this.clearFabricVariantSelection();
      this.resetProductMediaToDefault();
      requestAnimationFrame(() => {
        this.clearFabricVariantSelection();
        this.resetProductMediaToDefault();
      });
      window.setTimeout(() => {
        this.clearFabricVariantSelection();
        this.resetProductMediaToDefault();
      }, 50);
    }

    /**
     * Modal fabrics are not product variants — show the product's default
     * (non-variant) gallery image instead of the last variant photo.
     */
    resetProductMediaToDefault() {
      const root = this.getProductRoot();
      const gallery = root?.querySelector?.('media-gallery');
      if (!gallery) return;

      if (typeof gallery.init === 'function') {
        try {
          gallery.init();
        } catch (e) {
          /* gallery may already be initializing */
        }
      }

      const mediaId = this.dataset.defaultMediaId;
      const defaultSrc = this.dataset.defaultMediaSrc || '';
      const slides = Array.from(
        gallery.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
      );

      let slideIndex = -1;
      if (mediaId) {
        slideIndex = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      }

      if (slideIndex >= 0) {
        if (gallery.sliderInstance?.slider) {
          gallery.sliderInstance.slider.slideTo(slideIndex, 0, false);
        } else if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia({
            featured_media: {
              id: mediaId,
              position: Number(this.dataset.defaultMediaPosition || 1),
              media_type: 'image'
            }
          });
        }
        return;
      }

      // Default media isn't in the current gallery (often all slides are
      // variant photos) — swap the visible main image to the product default.
      if (defaultSrc) {
        this.applyDefaultMediaSrcToGallery(gallery, slides[0] || gallery, defaultSrc, mediaId);
        if (gallery.sliderInstance?.slider) {
          gallery.sliderInstance.slider.slideTo(0, 0, false);
        }
      } else if (gallery.sliderInstance?.slider) {
        gallery.sliderInstance.slider.slideTo(0, 0, false);
      }
    }

    applyDefaultMediaSrcToGallery(gallery, slide, src, mediaId) {
      if (!slide || !src) return;

      if (mediaId) slide.setAttribute('data-media-id', mediaId);
      slide.setAttribute('data-src', src);

      slide.querySelectorAll('img').forEach((img) => {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        if (img.getAttribute('src') !== src) img.src = src;
        img.loading = 'eager';
      });

      // Keep matching thumbnail in sync when present.
      const thumb =
        (mediaId && gallery.querySelector(`.product__thumbs-item[data-target="${mediaId}"] img`)) ||
        gallery.querySelector('.product__thumbs-item.swiper-slide-thumb-active img') ||
        gallery.querySelector('.product__thumbs-item img');
      if (thumb) {
        thumb.removeAttribute('srcset');
        thumb.src = src;
      }
    }

    unlockModalFabric() {
      this.modalFabricSelected = false;
      this.dataset.modalFabricSelected = 'false';
      this.dataset.fromVariant = 'true';
      try {
        sessionStorage.removeItem(this.storageKey());
      } catch (e) {
        /* ignore */
      }
    }

    readCatalog() {
      const el = this.querySelector('[data-fabric-catalog]');
      if (!el) return {};
      try {
        return JSON.parse(el.textContent || '{}');
      } catch (e) {
        return {};
      }
    }

    /**
     * Instant update when the fabric variant option changes — before the
     * theme's section fetch finishes (that was causing the slow image swap).
     */
    onVariantOptionChange(event) {
      const input = event.target;
      if (!input || (input.type !== 'radio' && input.tagName !== 'SELECT')) return;

      const root = this.getProductRoot();
      const variantSelects = input.closest?.('variant-selects');
      if (!variantSelects || (root && root !== document && !root.contains(variantSelects))) return;

      const wrap = input.closest('.product-form__input');
      if (!wrap || !isFabricOptionWrap(wrap, this.fabricOptionName, this.catalog)) return;

      const fabricName = input.type === 'radio' ? input.value : input.value;
      if (!fabricName) return;

      this.unlockModalFabric();
      wrap.removeAttribute('data-fabric-option-cleared');

      this.applyFromVariantOption(fabricName, input);
    }

    applyFromVariantOption(fabricName, input) {
      const fromCatalog = this.catalog[fabricName] || {};
      // Prefer already-in-DOM / catalog URLs so the 40px thumb updates instantly
      // (variantChange used the large gallery image after a section fetch — felt slow).
      const image =
        input?.dataset?.previewImage ||
        fromCatalog.image ||
        extractSwatchImage(input) ||
        '';

      this.applySelection(
        {
          id: fromCatalog.id || '',
          name: fabricName,
          collection: fromCatalog.collection || '',
          image
        },
        { fromModal: false }
      );
    }

    onVariantChange(event) {
      // Prefer the immediate change handler; this only fills gaps (e.g. URL variant load).
      if (this.modalFabricSelected || this.dataset.modalFabricSelected === 'true') {
        this.clearFabricVariantSelection();
        return;
      }

      const variant = event?.data?.variant;
      if (!variant) return;

      const optionName = this.fabricOptionName;
      let fabricName = '';
      let fabricInput = null;

      if (optionName && Array.isArray(variant.options)) {
        const root = this.getProductRoot();
        const selects = root?.querySelector('variant-selects');
        const fieldsets = selects?.querySelectorAll('.product-form__input') || [];
        fieldsets.forEach((fs) => {
          if (!isFabricOptionWrap(fs, optionName, this.catalog)) return;
          const checked = fs.querySelector('input[type="radio"]:checked');
          const select = fs.querySelector('select');
          fabricInput = checked || select;
          fabricName = checked?.value || select?.value || '';
        });
      }

      if (!fabricName && Array.isArray(variant.options) && variant.options.length === 1) {
        fabricName = variant.options[0];
      }

      if (!fabricName && Array.isArray(variant.options)) {
        for (const value of variant.options) {
          if (this.catalog[value]) {
            fabricName = value;
            break;
          }
        }
      }

      if (!fabricName) return;

      // Skip if already showing this fabric (avoid late overwrite with slower gallery URL).
      if (this.selectedNameEl?.textContent?.trim() === fabricName) return;

      const fromCatalog = this.catalog[fabricName] || {};
      const image =
        fromCatalog.image ||
        extractSwatchImage(fabricInput) ||
        (variant.featured_image?.src
          ? variant.featured_image.src
          : variant.featured_media?.preview_image?.src || '');

      this.applySelection(
        {
          id: fromCatalog.id || '',
          name: fabricName,
          collection: fromCatalog.collection || '',
          image
        },
        { fromModal: false }
      );
    }

    onFabricApplied(event) {
      if (!event.detail) return;
      this.lockModalFabric(event.detail);
    }

    clearFabricVariantSelection() {
      unselectFabricVariantOptions(this.formId, this.fabricOptionName, this.catalog);
    }

    applySelection({ id, name, collection, image } = {}, { fromModal = false } = {}) {
      const display = collection ? `${name} · ${collection}` : name;

      if (this.selectedNameEl) {
        this.selectedNameEl.textContent = name || '—';
      }

      this.updateSelectedImage(image);

      if (this.propertyInput) this.propertyInput.value = display || '';
      if (this.idPropertyInput) this.idPropertyInput.value = id || '';
      if (this.collectionPropertyInput) this.collectionPropertyInput.value = collection || '';

      if (fromModal) {
        // Keep cart line property; do not select a variant option radio.
      }
    }

    updateSelectedImage(image) {
      if (!this.selectedImageEl) return;

      if (!image) return;

      if (this.selectedImageEl.tagName === 'IMG') {
        const current = this.selectedImageEl.getAttribute('src') || '';
        if (current === image || this.selectedImageEl.src === image) return;
        this.selectedImageEl.loading = 'eager';
        this.selectedImageEl.decoding = 'async';
        this.selectedImageEl.src = image;
        this.selectedImageEl.hidden = false;
        return;
      }

      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.width = 40;
      img.height = 40;
      img.loading = 'eager';
      img.decoding = 'async';
      img.setAttribute('data-fabric-selected-image', '');
      this.selectedImageEl.replaceWith(img);
      this.selectedImageEl = img;
    }

    getProductRoot() {
      const form = document.getElementById(this.formId);
      return form?.closest('product-info') || document;
    }
  }

  function extractSwatchImage(input) {
    if (!input) return '';

    const label =
      input.labels?.[0] ||
      (input.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null) ||
      input.closest('.product-form__input')?.querySelector(`label[for="${input.id}"]`);

    if (!label) return '';

    const preview = label.querySelector('.swatch-preview__image');
    const fromPreview = urlFromCssImage(preview?.style?.backgroundImage);
    if (fromPreview) return fromPreview;

    const swatch = label.querySelector('.swatch-color');
    const swatchVar = swatch?.style?.getPropertyValue('--swatch-background') || '';
    return urlFromCssImage(swatchVar);
  }

  function urlFromCssImage(value) {
    if (!value) return '';
    const match = String(value).match(/url\(\s*['"]?(.*?)['"]?\s*\)/i);
    return match?.[1] || '';
  }

  class FabricModal extends HTMLElement {
    connectedCallback() {
      this.modalId = this.dataset.modalId;
      this.formId = this.dataset.formId;
      this.propertyName = this.dataset.propertyName || 'Tkanina';
      this.content = this.querySelector('[data-fabric-content]');
      this.footerName = this.querySelector('[data-fabric-footer-name]');
      this.footerImage = this.querySelector('[data-fabric-footer-image]');
      this.applyBtn = this.querySelector('[data-fabric-apply]');
      this.navItems = Array.from(this.querySelectorAll('[data-fabric-nav]'));
      this.sections = Array.from(this.querySelectorAll('[data-fabric-collection-section]'));

      this.pending = {
        id: this.dataset.initialId || '',
        name: '',
        collection: '',
        image: ''
      };

      const initialSwatch =
        this.querySelector(`.fabric-modal__swatch.is-selected`) ||
        this.querySelector('[data-fabric-swatch]');
      if (initialSwatch) {
        this.pending = readSwatchData(initialSwatch);
      }

      this.onNavClick = this.onNavClick.bind(this);
      this.onSwatchClick = this.onSwatchClick.bind(this);
      this.onApply = this.onApply.bind(this);
      this.onViewToggle = this.onViewToggle.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.navItems.forEach((btn) => btn.addEventListener('click', this.onNavClick));
      this.querySelectorAll('[data-fabric-swatch]').forEach((btn) =>
        btn.addEventListener('click', this.onSwatchClick)
      );
      this.applyBtn?.addEventListener('click', this.onApply);
      this.querySelectorAll('[data-fabric-view]').forEach((btn) =>
        btn.addEventListener('click', this.onViewToggle)
      );
      this.content?.addEventListener('scroll', this.onScroll, { passive: true });

      this.modalEl = document.getElementById(this.modalId);
      this._onModalShow = this.onModalShow.bind(this);
      this.modalEl?.addEventListener('modal:handleAfterShow', this._onModalShow);

      this.scrollingFromNav = false;
    }

    disconnectedCallback() {
      this.modalEl?.removeEventListener('modal:handleAfterShow', this._onModalShow);
    }

    onModalShow() {
      const selector = document.querySelector(
        `fabric-selector[data-modal-id="${this.modalId}"]`
      );
      const idInput = selector?.querySelector('[data-fabric-id-property]');
      const nameEl = selector?.querySelector('[data-fabric-selected-name]');
      const collectionInput = selector?.querySelector('[data-fabric-collection-property]');
      const imageEl = selector?.querySelector('[data-fabric-selected-image]');

      const name = nameEl?.textContent?.trim() || '';
      const id = idInput?.value || '';

      // Prefer catalog id match by name when coming from variant (id may be empty)
      let pendingId = id;
      if (!pendingId && name) {
        const swatchByName = this.querySelector(
          `[data-fabric-swatch][data-fabric-name="${CSS.escape(name)}"]`
        );
        if (swatchByName) {
          this.pending = readSwatchData(swatchByName);
          this.highlightSwatch(this.pending.id);
          this.updateFooter(this.pending);
          this.scrollToSwatch(swatchByName);
          return;
        }
      }

      if (pendingId || name) {
        this.pending = {
          id: pendingId,
          name,
          collection: collectionInput?.value || '',
          image: imageEl?.tagName === 'IMG' ? imageEl.src : ''
        };
        if (pendingId) this.highlightSwatch(pendingId);
        this.updateFooter(this.pending);

        const swatch = pendingId
          ? this.querySelector(`[data-fabric-swatch][data-fabric-id="${CSS.escape(pendingId)}"]`)
          : null;
        if (swatch) this.scrollToSwatch(swatch);
      }
    }

    scrollToSwatch(swatch) {
      const section = swatch?.closest('[data-fabric-collection-section]');
      if (!section || !this.content) return;
      const nav = this.navItems.find(
        (n) => n.dataset.collectionHandle === section.dataset.collectionHandle
      );
      if (nav) this.setActiveNav(nav);
      this.content.scrollTop = Math.max(0, section.offsetTop - 8);
    }

    onNavClick(event) {
      const btn = event.currentTarget;
      const targetId = btn.dataset.fabricNav;
      const section = this.querySelector(`#${CSS.escape(targetId)}`);
      if (!section || !this.content) return;

      this.setActiveNav(btn);
      this.scrollingFromNav = true;
      const top = section.offsetTop - 8;
      this.content.scrollTo({ top, behavior: 'smooth' });
      window.setTimeout(() => {
        this.scrollingFromNav = false;
      }, 500);
    }

    onScroll() {
      if (this.scrollingFromNav || !this.content) return;

      const scrollTop = this.content.scrollTop + 48;
      let current = this.sections[0];
      for (const section of this.sections) {
        if (section.offsetTop <= scrollTop) current = section;
      }
      if (!current) return;

      const handle = current.dataset.collectionHandle;
      const nav = this.navItems.find((n) => n.dataset.collectionHandle === handle);
      if (nav) this.setActiveNav(nav);
    }

    setActiveNav(activeBtn) {
      this.navItems.forEach((btn) => {
        btn.classList.toggle(ACTIVE_CLASS, btn === activeBtn);
      });
    }

    onSwatchClick(event) {
      const swatch = event.currentTarget;
      this.pending = readSwatchData(swatch);
      this.highlightSwatch(this.pending.id);
      this.updateFooter(this.pending);
    }

    highlightSwatch(id) {
      this.querySelectorAll('[data-fabric-swatch]').forEach((el) => {
        const selected = el.dataset.fabricId === id;
        el.classList.toggle(SELECTED_CLASS, selected);
        el.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    updateFooter({ name, collection, image }) {
      if (this.footerName) {
        this.footerName.textContent = collection ? `${name} · ${collection}` : name || '—';
      }
      if (this.footerImage) {
        if (this.footerImage.tagName === 'IMG') {
          if (image) {
            this.footerImage.src = image;
            this.footerImage.hidden = false;
          }
        } else if (image) {
          const img = document.createElement('img');
          img.src = image;
          img.alt = '';
          img.width = 44;
          img.height = 44;
          img.loading = 'lazy';
          img.setAttribute('data-fabric-footer-image', '');
          this.footerImage.replaceWith(img);
          this.footerImage = img;
        }
      }
    }

    onViewToggle(event) {
      const btn = event.currentTarget;
      const view = btn.dataset.fabricView;
      this.querySelectorAll('[data-fabric-view]').forEach((el) => {
        const active = el === btn;
        el.classList.toggle(ACTIVE_CLASS, active);
        el.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.classList.toggle('fabric-modal__root--dense', view === 'dense');
    }

    onApply() {
      if (!this.pending?.name) return;

      document.dispatchEvent(
        new CustomEvent('fabric:applied', {
          detail: { ...this.pending }
        })
      );

      const modal = document.getElementById(this.modalId);
      if (modal && typeof modal.hide === 'function') {
        modal.hide();
      }
    }
  }

  function readSwatchData(swatch) {
    return {
      id: swatch.dataset.fabricId || '',
      name: swatch.dataset.fabricName || '',
      collection: swatch.dataset.fabricCollection || '',
      image: swatch.dataset.fabricImage || ''
    };
  }

  /**
   * Clear selected state on the fabric-related variant option
   * (radios unchecked / select cleared) after modal apply.
   */
  function unselectFabricVariantOptions(formId, fabricOptionName, catalog) {
    const form = document.getElementById(formId);
    const root = form?.closest('product-info') || form?.closest('[id^="MainProduct"]') || document;
    const variantSelectsList = root.querySelectorAll
      ? root.querySelectorAll('variant-selects')
      : [];

    const targets =
      variantSelectsList.length > 0
        ? variantSelectsList
        : document.querySelectorAll('variant-selects');

    targets.forEach((variantSelects) => {
      clearFabricOptionInVariantSelects(variantSelects, fabricOptionName, catalog);
    });
  }

  function clearFabricOptionInVariantSelects(variantSelects, fabricOptionName, catalog) {
    if (!variantSelects) return;

    const wraps = Array.from(variantSelects.querySelectorAll('.product-form__input'));
    let matched = wraps.filter((inputWrap) =>
      isFabricOptionWrap(inputWrap, fabricOptionName, catalog)
    );

    // Last resort: the color/swatch picker on PDP (fabric is usually that row).
    if (!matched.length) {
      matched = wraps.filter((inputWrap) =>
        /product-form__input--(color|swatch)/.test(inputWrap.className)
      );
    }

    matched.forEach((inputWrap) => clearFabricOptionWrap(inputWrap));

    // Direct radio name match (options[{{name}}] uses name="Tkanina-1")
    if (fabricOptionName) {
      const safe = cssEscape(fabricOptionName);
      variantSelects.querySelectorAll(`input[type="radio"][name^="${safe}-"]`).forEach((radio) => {
        radio.checked = false;
        radio.removeAttribute('checked');
        const wrap = radio.closest('.product-form__input');
        if (wrap) clearFabricOptionWrap(wrap);
      });
    }
  }

  function clearFabricOptionWrap(inputWrap) {
    if (!inputWrap) return;

    inputWrap.setAttribute('data-fabric-option-cleared', 'true');

    inputWrap.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.checked = false;
      radio.removeAttribute('checked');
    });

    const select = inputWrap.querySelector('select');
    if (select) {
      select.selectedIndex = -1;
      select.value = '';
      select.querySelectorAll('option[selected]').forEach((opt) => opt.removeAttribute('selected'));
    }

    const selectedLabel = inputWrap.querySelector('[data-selected-swatch-value]');
    if (selectedLabel) selectedLabel.textContent = '';
  }

  function normalizeOptionLabel(text) {
    return (text || '')
      .replace(/[:：]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function cssEscape(value) {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/([^a-zA-Z0-9_-])/g, '\\$1');
  }

  function getOptionNameFromWrap(inputWrap) {
    const byAttr = inputWrap.querySelector('[data-selected-swatch-value]');
    if (byAttr?.hasAttribute('data-selected-swatch-value')) {
      const attr = byAttr.getAttribute('data-selected-swatch-value');
      if (attr) return attr;
    }

    const nameSpan = inputWrap.querySelector(
      '.form__label .font-body-bolder, legend .font-body-bolder, .form__label > span'
    );
    if (nameSpan) return nameSpan.textContent || '';

    const radio = inputWrap.querySelector('input[type="radio"][name]');
    if (radio?.name) {
      // "Tkanina-1" → "Tkanina"
      return radio.name.replace(/-\d+$/, '');
    }

    return '';
  }

  function isFabricOptionWrap(inputWrap, fabricOptionName, catalog) {
    const optionName = normalizeOptionLabel(getOptionNameFromWrap(inputWrap));

    if (fabricOptionName) {
      const target = normalizeOptionLabel(fabricOptionName);
      if (
        optionName &&
        target &&
        (optionName === target || optionName.startsWith(target) || target.startsWith(optionName))
      ) {
        return true;
      }

      // data-selected-swatch-value="{{ option.name }}"
      const swatchValueEl = inputWrap.querySelector(
        `[data-selected-swatch-value="${cssEscape(fabricOptionName)}"]`
      );
      if (swatchValueEl) return true;

      const safe = cssEscape(fabricOptionName);
      if (inputWrap.querySelector(`input[type="radio"][name^="${safe}-"]`)) return true;
    }

    // Fallback: option values appear in fabric catalog
    const values = [
      ...Array.from(inputWrap.querySelectorAll('input[type="radio"]')).map((r) => r.value),
      ...Array.from(inputWrap.querySelectorAll('select option')).map((o) => o.value)
    ].filter(Boolean);

    return values.some((value) => Boolean(catalog?.[value]));
  }

  if (!customElements.get('fabric-selector')) {
    customElements.define('fabric-selector', FabricSelector);
  }
  if (!customElements.get('fabric-modal')) {
    customElements.define('fabric-modal', FabricModal);
  }
})();
